import { useEffect } from 'react';
import { Button, ButtonVariants } from '@components/button/Button';
import { ContactInfo } from 'restaurantAdmin/components/contactInfo/ContactInfo';
import { WAITLIST_ENTRY_INDEX_PARAM } from 'restaurantAdmin/utils/constants';
import { useServiceSearchParams } from 'restaurantAdmin/reservations/service/hooks/useServiceSearchParams';
import { useWaitListContext } from 'restaurantAdmin/reservations/service/sidePanel/waitList/state/WaitListContext';
import { HeaderBar } from '../HeaderBar';
import styles from './SeatWaitlistGuestDrawer.scss';
import { getFullName } from '@shared/utils/formatName';
import { useReservationServiceSelectedSeatingContext } from '../../state/ReservationServiceSelectedSeatingContext';
import {
  ServicePageModalType,
  useServicePageModalContext,
} from 'restaurantAdmin/context/ServicePageModalContext';
import { useWalkInAvailabilities } from '../../walkIns/hooks';
import { errorToast } from '@components/toasts/Toasts';
import { reportAppError } from '@shared/reportAppError';
import { SeatingSelectedTables } from '../../SeatingSelectedTables';

export const SeatWaitlistGuestDrawer = () => {
  const { openWaitListEntryDrawer, searchParams } = useServiceSearchParams();

  const {
    selectedWaitListEntry,
    setSelectedWaitListEntryIndex,
    confirmSeatWaitListEntry,
  } = useWaitListContext();

  const { selectedFloorPlanTables } =
    useReservationServiceSelectedSeatingContext();
  const { openModal } = useServicePageModalContext();

  useEffect(() => {
    setSelectedWaitListEntryIndex(
      Number.parseInt(searchParams.get(WAITLIST_ENTRY_INDEX_PARAM) || '0', 10),
    );
  }, []);

  const handleCancelSeating = () => {
    const index = Number.parseInt(
      searchParams.get(WAITLIST_ENTRY_INDEX_PARAM) || '0',
      10,
    );
    openWaitListEntryDrawer(index);
  };

  const { data: walkInAvailabilities, error: walkInAvailabilityFetchError } =
    useWalkInAvailabilities({
      floorPlanTables: selectedFloorPlanTables,
      guestCount: selectedWaitListEntry?.guestCount,
    });

  if (!selectedWaitListEntry) return null;

  const onSubmit = async () => {
    if (walkInAvailabilityFetchError) {
      errorToast({
        message: 'Failed to confirm seat availability. Please try again.',
      });
      reportAppError(walkInAvailabilityFetchError);
      return;
    }

    if (walkInAvailabilities.some((a) => !a.hasAvailability)) {
      openModal(ServicePageModalType.WalkInSeatingConflict, {
        handleConfirm: () => {
          void confirmSeatWaitListEntry(selectedFloorPlanTables);
        },
      });
      return;
    }

    await confirmSeatWaitListEntry(selectedFloorPlanTables);
  };

  return (
    <div className={styles.container} aria-label="Seat waitlist guest">
      <HeaderBar
        name={getFullName(
          selectedWaitListEntry.firstName,
          selectedWaitListEntry.lastName,
        )}
        guestCount={selectedWaitListEntry.guestCount}
      />

      <div className={styles.waitlistDetails}>
        <Button
          label="Seat Guest"
          onClick={onSubmit}
          variant={ButtonVariants.Primary}
          isDisabled={selectedFloorPlanTables.length === 0}
        />
        <Button
          label="Cancel Seating"
          onClick={handleCancelSeating}
          variant={ButtonVariants.Tertiary}
        />

        <ContactInfo phone={selectedWaitListEntry.phone} />
        <SeatingSelectedTables floorPlanTables={selectedFloorPlanTables} />
      </div>
    </div>
  );
};
